import { useCallback, useState } from 'react'
import { Checkbox, Dialog, InputGroup, Radio, RadioGroup, Label } from '@blueprintjs/core'
import { ExportType } from 'utils/hid-export'
import { Colors } from 'design/colors'
import { downloadData, filenameWithTimestamp } from 'utils/common'
import { ProxyMarked, proxy } from 'comlink'
import { usePacketMetadata } from 'hooks/usePacketMetadata'
import { TimeWindow, usePlayback } from 'hooks/usePlayback'
import { ClickableIcon } from '../ClickableIcon'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { packetThreadPoolApi, realtimeMetadata } from 'utils/packet-workers'
import { Button } from '../Button'

const defaultFilename = (): string => filenameWithTimestamp('recording')

export const FileExport = (props: { onClose: () => void }) => {
  const { onClose } = props
  const { timeWindow } = usePlayback()
  const [exportSize, setExportSize] = useState<'all' | 'selection'>('all')
  const [isExporting, setIsExporting] = useState(false)

  // form state
  const [filename, setFilename] = useState(defaultFilename())
  const [playbackChecked, setPlaybackChecked] = useState(true)
  const [csvChecked, setCsvChecked] = useState(false)

  const exportEnabled = filename.length > 0 && (csvChecked || playbackChecked) && !isExporting

  const handleExport = useCallback(
    async (
      downloadFn: ((filename: string, text: string) => void) & ProxyMarked,
      exportWindow: TimeWindow
    ) => {
      setIsExporting(true)
      try {
        // TODO use Promise.allSettled below
        if (playbackChecked) {
          await packetThreadPoolApi.export(filename, ExportType.Playback, exportWindow, downloadFn)
        }

        if (csvChecked) {
          await packetThreadPoolApi.export(filename, ExportType.CSV, exportWindow, downloadFn)
        }
      } finally {
        setIsExporting(false)
        onClose()
      }
    },
    [csvChecked, filename, onClose, playbackChecked]
  )

  const onExportClick = useCallback(() => {
    if (filename.length === 0) {
      return
    }

    let exportWindow: TimeWindow
    if (exportSize === 'all') {
      exportWindow = realtimeMetadata.timeWindow || [0, Infinity]
    } else if (timeWindow === undefined) {
      return
    } else {
      exportWindow = timeWindow
    }

    let fileNames: { [fileName: string]: true } = {}

    // TODO HACK: downloadDataOnce is a workaround for an issue where
    // export() in the packet.worker API is being called twice even though
    // it's called only once in packet-workers.ts. To understand it, I'd
    // probably have to dig into the internals of the comlink library.
    const downloadDataOnce = (fileName: string, data: string | Uint8Array) => {
      if (fileNames[fileName]) return // Do not downloadData again
      fileNames[fileName] = true
      downloadData(fileName, data)
    }
    const downloadFn = proxy(downloadDataOnce)

    return handleExport(downloadFn, exportWindow)
  }, [filename.length, timeWindow, exportSize, handleExport])

  return (
    <Dialog
      title="Export"
      isOpen={true}
      onClose={props.onClose}
      style={{ backgroundColor: Colors.LIGHT_GREEN }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          margin: 30
        }}
      >
        <InputGroup
          placeholder="Name"
          value={filename}
          onChange={(e) => setFilename(e.currentTarget.value)}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: 20,
            color: Colors.WHITE
          }}
        >
          {/* Data to export select area */}
          <div style={{ marginBottom: 10 }}>
            <Label style={{ marginBottom: 10 }}>What to export</Label>
            <RadioGroup
              inline={false}
              selectedValue={exportSize}
              onChange={(e) => setExportSize(e.currentTarget.value as 'all' | 'selection')}
            >
              <Radio label="All data points" value="all" />
              <Radio
                label="Data points in view"
                value="selection"
                disabled={timeWindow === undefined}
              />
            </RadioGroup>
          </div>
          {/* File type select area */}
          <div style={{ marginBottom: 10 }}>
            <Label style={{ marginBottom: 10 }}>File type(s)</Label>
            <Checkbox
              inline={false}
              label="Playback file"
              checked={playbackChecked}
              onChange={(e) => setPlaybackChecked(e.currentTarget.checked)}
            />
            <Checkbox
              inline={false}
              label="CSV"
              checked={csvChecked}
              onChange={(e) => setCsvChecked(e.currentTarget.checked)}
            />
          </div>
        </div>
        <div style={{ alignSelf: 'flex-end', display: 'flex' }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            style={{ marginRight: 10 }}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            disabled={!exportEnabled}
            onClick={onExportClick}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export const ExportButton = () => {
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const totalPackets = usePacketMetadata().length

  if (totalPackets === 0) {
    return null
  }

  return (
    <>
      {exportDialogOpen && <FileExport onClose={() => setExportDialogOpen(false)} />}
      <ClickableIcon
        icon={faDownload}
        title="Export"
        color={Colors.WHITE}
        size="xl"
        onClick={() => setExportDialogOpen(true)}
      />
    </>
  )
}
